import { getUnsubscribedUsers, inviteUserNew } from "frontend/api";
import { useState, useEffect } from "react";
import { Permission, User, Account, Board, BoardMembers } from "shared/datamodel/schemas";
import { useFeatureFlag } from "./use-feature-flag/use-feature-flag";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { Project } from "shared/datamodel/schemas/project";
import { State, Team } from "shared/datamodel/schemas/team";
import type { BubbleFieldMutation, BubbleFieldValue } from "frontend/ui-components/form-fields/form-bubbles-field";
import { handleInviteEmailMutation, trackInviteCompletedAccount } from "utils/invite-utils";
import { useUsersCache } from "./caching/use-users-cache";
import { useInstanceMembers } from "./use-instance-members";
import tracking from "frontend/tracking";

const getInstanceId = (instance: Account | Team | Project | Board, instanceType: InstanceType) => {
  switch (instanceType) {
    case InstanceType.Board:
    case InstanceType.Project:
    case InstanceType.Team:
    case InstanceType.Workspace: {
      return (instance as Board | Project | Team).documentId;
    }
    case InstanceType.Account: {
      return (instance as Account).id.toString();
    }
  }
};

export default function useInviteNew({
  bulkPermission,
  instance,
  instanceType,
  source,
  parentInstance,
  isFreePlan,
}: {
  bulkPermission: Permission;
  instance: Account | Team | Project | Board;
  instanceType: InstanceType;
  source: string;
  parentInstance?: Team;
  isFreePlan?: boolean;
}) {
  const isPerSeatLicenseEnabled = useFeatureFlag("per-seat-license");
  const [users, setUsers] = useState<User[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const [addedSeats, setAddedSeats] = useState<number>(0);
  const [highlightInvalidEmails, setHighlightInvalidEmails] = useState(false);
  const [emails, setEmails] = useState<BubbleFieldValue[]>([]);
  const [loading, setLoading] = useState(false);
  const { updateUsersCache } = useUsersCache();
  const { updateMembers, members: instanceMembers } = useInstanceMembers(
    getInstanceId(instance, instanceType),
    instanceType
  );
  const validEmails = emails.filter((v) => v.isValid).map((v) => v.value.toLowerCase());
  const [unsubscribed, setUnsubscribed] = useState<string[] | null>(null);
  const [shouldRenderUnsubscribedModal, setShouldRenderUnsubscribedModal] = useState(false);
  const [isInviteClicked, setIsInviteClicked] = useState(false);
  const [addAsRestricted, setAddAsRestricted] = useState<boolean | null>(null);

  useEffect(() => {
    if (unsubscribed && unsubscribed.length > 0 && addAsRestricted === null) {
      setShouldRenderUnsubscribedModal(true);
    } else if (isInviteClicked && (addAsRestricted !== null || unsubscribed?.length === 0)) {
      setIsInviteClicked(false);
      handleInviteClick(addAsRestricted || false);
    }
  }, [unsubscribed, handleInviteClick, isInviteClicked, addAsRestricted, loading]);

  function handleInviteFlow() {
    setIsInviteClicked(true);
    if (isFreePlan) {
      setAddAsRestricted(false);
    } else {
      checkUnsubscribedUsers();
    }
  }

  function getInstanceIdObject() {
    switch (instanceType) {
      case InstanceType.Board: {
        return { documentId: (instance as Board).documentId };
      }
      case InstanceType.Project: {
        return { projectId: (instance as Project).documentId };
      }
      case InstanceType.Workspace:
      case InstanceType.Team: {
        return { teamId: (instance as Team).documentId };
      }
    }
  }

  async function handleInviteClick(addAsRestricted: boolean) {
    setLoading(true);
    await onInviteClick(addAsRestricted);
    setLoading(false);
  }

  function updateCaches(users: User[], bulkPermission: Permission) {
    updateUsersCache(users);
    switch (instanceType) {
      case InstanceType.Board: {
        const boardMembers: BoardMembers = {
          documentId: (instance as Board).documentId,
          members: [
            ...instanceMembers.map((m) => ({
              id: Number(m.id),
              permission: m.permission,
              deleted: false,
            })),
            ...users.map((u) => ({
              id: Number(u.id),
              permission: bulkPermission,
              deleted: false,
            })),
          ],
        };
        updateMembers(boardMembers);
        return { documentId: (instance as Board).documentId };
      }
      case InstanceType.Project: {
        const newUsers = users.map((u) => ({
          id: u.id,
          state: State.active,
          permission: bulkPermission,
        }));
        const projectInstance = instance as Project;
        return updateMembers(
          {
            ...projectInstance,
            userProjectPermissions: [...projectInstance.userProjectPermissions, ...newUsers],
          },
          parentInstance
        );
      }
      case InstanceType.Team:
      case InstanceType.Workspace: {
        const newUsers = users.map((u) => ({
          id: u.id,
          state: State.active,
          permission: bulkPermission,
        }));
        const teamInstance = instance as Team;
        return updateMembers({
          ...teamInstance,
          userTeamPermissions: [...teamInstance.userTeamPermissions, ...newUsers],
        });
      }
    }
  }

  async function checkUnsubscribedUsers() {
    setLoading(true);
    const unsubscribed = await getUnsubscribedUsers(validEmails, isPerSeatLicenseEnabled);
    setUnsubscribed(unsubscribed);
    setLoading(false);
  }

  async function onInviteClick(addAsRestricted: boolean) {
    const { users, failed, addedSeats, invitedToAccountAmount, invitedToAccountFailedAmount } = await inviteUserNew({
      emails: validEmails,
      bulkPermission,
      ...getInstanceIdObject(),
      isPerSeatLicenseEnabled,
      addAsRestricted,
    });
    const userEmails = new Set(users.map((u) => u.email));
    setEmails((previousEmails) => previousEmails.filter((email) => !userEmails.has(email.value)));
    setUsers(users);
    if (users.length > 0) {
      updateCaches(users, bulkPermission);
    }
    setFailed(failed);
    setAddedSeats(addedSeats);
    trackInviteCompletedAccount(invitedToAccountAmount, invitedToAccountFailedAmount, source);

    if (users.length > 0 && instanceType !== InstanceType.Account) {
      tracking.trackAnalyticsEvent(`invite_completed_${instanceType}`, {
        source,
        invited_users: users.length,
        failed_users: failed.length,
      });
    }
  }

  function changeEmails(bubbleFieldMutation: BubbleFieldMutation, source: string) {
    handleInviteEmailMutation(bubbleFieldMutation, source, setEmails, setHighlightInvalidEmails);
  }

  return {
    users,
    failed,
    addedSeats,
    onInviteClick,
    changeEmails,
    emails,
    highlightInvalidEmails,
    handleInviteClick,
    checkUnsubscribedUsers,
    loading,
    unsubscribed,
    setUnsubscribed,
    shouldRenderUnsubscribedModal,
    setShouldRenderUnsubscribedModal,
    addAsRestricted,
    setAddAsRestricted,
    handleInviteFlow,
  };
}
